import {hpcAttrs} from '@github-ui/hpc'
import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {BaseStyles} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useSyncExternalStore} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import {RoadmapDateFieldNone} from '../../../api/view/contracts'
import {ObserverProvider} from '../../../components/board/hooks/use-is-visible'
import {BulkAddNotifications} from '../../../components/bulk-add-notifications'
import {BaseProjectViewFilterInput} from '../../../components/filter-bar/base-project-view-filter-input'
import {ROADMAP_ROW_HEIGHT, roadmapCssVariables} from '../../../components/roadmap/constants'
import {PROJECT_ITEMS_REGION_ID} from '../../../components/slicer-panel/constants'
import {SlicerItemsProvider, useSlicerItems} from '../../../components/slicer-panel/slicer-items-provider'
import {SlicerPanel} from '../../../components/slicer-panel/slicer-panel'
import {AddColumnModal} from '../../../components/table/add-column-modal'
import {TableDragToFillProvider} from '../../../components/table/bulk-fill-values'
import {CopilotAssignmentModalProvider} from '../../../components/table/copilot-assignment-modal-provider'
import {CopyPasteProvider} from '../../../components/table/hooks/use-copy-paste'
import {useIsOmnibarFixed} from '../../../components/table/hooks/use-is-omnibar-fixed'
import {type TableFocusMeta, TableNavigationProvider, useTableNavigation} from '../../../components/table/navigation'
import {ReorderableRowsContext} from '../../../components/table/row-reordering/reorderable-rows'
import {RowReorderSash} from '../../../components/table/row-reordering/row-reorder-sash'
import {RowReorderingProvider} from '../../../components/table/row-reordering/row-reordering-provider'
import {TableCellBulkSelectionProvider} from '../../../components/table/table-cell-bulk-selection'
import {useItemData} from '../../../components/table/use-item-data'
import {useSelectionBlur} from '../../../components/table/use-selection-blur'
import {useTableFocus} from '../../../components/table/use-table-focus'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useSliceBy} from '../../../features/slicing/hooks/use-slice-by'
import {addNoGroupWhenGroupsDisabled} from '../../../helpers/table-group-utilities'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {getIsTableGrouped} from '../../../hooks/tanstack-table/helpers/table-api'
import {RoadmapPillAreaColumnDef} from '../../../hooks/tanstack-table/providers/columns/roadmap-pill-area-column-def'
import {TableProvider, useTableInstance} from '../../../hooks/tanstack-table/providers/table-provider'
import {useReactTable} from '../../../hooks/tanstack-table/use-react-table'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useInitialTableFocusAction} from '../../../hooks/use-initial-table-focus-action'
import {useMeasureScrollbars} from '../../../hooks/use-measure-scrollbars'
import {useRoadmapSettings, useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {LoadingStates, useViewLoadingState} from '../../../hooks/use-view-loading-state'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {useAddFieldModal} from '../../../state-providers/modals/use-add-field-modal'
import {useViewOptionsMenuRef} from '../../../state-providers/view-options/use-view-options-menu-ref'
import {useRoadmapHeaderHeight} from '../hooks/use-roadmap-header-height'
import {getHeaderFocus, makeFocusableColumnFilter, makeFocusableRowFilter} from '../roadmap-navigation-meta'
import {
  RoadmapViewProvider,
  useRoadmapIsScrollingLocked,
  useRoadmapNavigation,
  useRoadmapView,
} from '../roadmap-view-provider'
import {RoadmapControls} from './roadmap-controls'
import {GroupedRoadmapItems} from './roadmap-grouped-items'
import {RoadmapHeader} from './roadmap-header'
import {RoadmapItems} from './roadmap-items'
import {RoadmapMarkers} from './roadmap-marker-new'
import {RoadmapMarkersInRangeProvider} from './roadmap-markers-in-range'
import {RoadmapFixedOmnibar} from './roadmap-omnibar'
import styles from './roadmap-view.module.css'

interface RoadmapRef {
  focusIn: () => void
}

/**
 * Roadmap view component - a.k.a. Roadmap view.
 * This component is in the `/pages/roadmap/components` directory,
 * as oppposed to `/components` to align with future changes to our
 * directory structure as described in https://github.com/github/planning-tracking/issues/1007
 */

export const RoadmapView = memo(function RoadmapViewComponent({ref}: {ref?: React.Ref<RoadmapRef>}) {
  return (
    <RoadmapViewProvider>
      <SlicerItemsProvider>
        <RoadmapLayout ref={ref} />
      </SlicerItemsProvider>
    </RoadmapViewProvider>
  )
})

// Roadmap item rows are virtualized.  If not within the OBSERVER_Y_MARGIN of the viewport, render an empty placeholder row.
// Nested roadmap pills also conditionally rendered.  If outside the viewport X, render an empty pill and navigation arrow.
const OBSERVER_Y_MARGIN = 10 * ROADMAP_ROW_HEIGHT

/**
 * The main scrollable container of the Roadmap view, including the header
 */
function RoadmapLayout({ref: forwardedRef}: {ref?: React.Ref<RoadmapRef>}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {getTimeSpanFromColumnData} = useRoadmapSettings()
  const {allColumns} = useAllColumns()
  const {dateFields} = useRoadmapSettings()
  const fields = useMemo(() => {
    return [
      ...allColumns.filter(col => col.id === SystemColumnId.Title),
      ...dateFields.filter(f => f !== RoadmapDateFieldNone),
    ]
  }, [allColumns, dateFields])
  const tableProps = useMemo(
    () => ({
      fields,
      pinnedRightColumnDefs: [RoadmapPillAreaColumnDef],
    }),
    [fields],
  )
  const {sliceField} = useSliceBy()
  const {slicerItems, totalSlicerItemCount} = useSlicerItems()
  const table = useReactTable(tableProps)
  const flatRows = table.getRowModel().flatRows
  const metaRef = useRef<TableFocusMeta>({
    tableInstance: table,
    getHeaderFocus,
    focusableRowFilter: makeFocusableRowFilter({hasWritePermissions, getTimeSpanFromColumnData}),
    focusableColumnFilter: makeFocusableColumnFilter({hasWritePermissions, getTimeSpanFromColumnData}),
  })

  useEffect(() => {
    metaRef.current.focusableRowFilter = makeFocusableRowFilter({hasWritePermissions, getTimeSpanFromColumnData})
    metaRef.current.focusableColumnFilter = makeFocusableColumnFilter({
      hasWritePermissions,
      getTimeSpanFromColumnData,
    })
  }, [hasWritePermissions, getTimeSpanFromColumnData])

  return (
    <TableProvider table={table}>
      <CopilotAssignmentModalProvider>
        <TableNavigationProvider metaRef={metaRef}>
          <TableCellBulkSelectionProvider>
            <TableDragToFillProvider>
              <CopyPasteProvider>
                {sliceField && <SlicerPanel slicerItems={slicerItems} totalSlicerItemCount={totalSlicerItemCount} />}
                <BaseProjectViewFilterInput />
                <BulkAddNotifications />
                <RoadmapMarkersInRangeProvider rows={flatRows}>
                  <RoadmapInner ref={forwardedRef} />
                </RoadmapMarkersInRangeProvider>
              </CopyPasteProvider>
            </TableDragToFillProvider>
          </TableCellBulkSelectionProvider>
        </TableNavigationProvider>
      </CopilotAssignmentModalProvider>
    </TableProvider>
  )
}

function RoadmapInner({ref: forwardedRef}: {ref?: React.Ref<RoadmapRef>}) {
  const tableInstance = useTableInstance()
  const isScrollingLocked = useRoadmapIsScrollingLocked()
  const {onKeyDown, onBlur} = useTableFocus()
  const {collapsedGroups} = useHorizontalGroupedBy()
  const {today} = useRoadmapView()
  const tableWidth = useRoadmapTableWidth()
  const headerHeight = useRoadmapHeaderHeight()
  const {roadmapRef, scrollToDate, shiftToNextRange, shiftToPrevRange} = useRoadmapNavigation()
  const {horizontalScrollbarSize} = useMeasureScrollbars(roadmapRef)
  const topLevelRows = tableInstance.getPreExpandedRowModel().rows
  const expandedRows = tableInstance.getExpandedRowModel().rows
  const isGrouped = getIsTableGrouped(tableInstance)
  const containerRef = useRef<HTMLDivElement>(null)
  const {loadingState} = useViewLoadingState()
  const {hasWritePermissions} = ViewerPrivileges()
  const {setShowAddFieldModal, showAddFieldModal} = useAddFieldModal()
  const {anchorRef} = useViewOptionsMenuRef()
  const {navigationDispatch} = useTableNavigation()
  const {memex_disable_autofocus, memex_small_viewport_a11y} = useEnabledFeatures()

  const initialFocusAction = useInitialTableFocusAction(tableInstance)
  const focusIn = useCallback(() => {
    if (initialFocusAction) navigationDispatch(initialFocusAction)
  }, [navigationDispatch, initialFocusAction])

  // focus in on init
  useEffect(() => {
    if (!memex_disable_autofocus) focusIn()
  }, [focusIn, memex_disable_autofocus])

  useImperativeHandle(forwardedRef, () => ({focusIn}))

  const effectiveTableHeight =
    loadingState === LoadingStates.loaded
      ? expandedRows.length * ROADMAP_ROW_HEIGHT + headerHeight
      : ROADMAP_ROW_HEIGHT + headerHeight

  const showFixedOmnibar = useIsOmnibarFixed({
    isGrouped,
    totalHeight: effectiveTableHeight,
    scrollRef: containerRef,
  })

  const shouldScrollToToday = useRef(true)
  useEffect(() => {
    if (shouldScrollToToday.current) {
      shouldScrollToToday.current = false
      scrollToDate(today)
    }
  }, [today, scrollToDate])

  const onScroll = useCallback(() => {
    if (roadmapRef?.current) {
      const {scrollLeft, scrollWidth, clientWidth} = roadmapRef.current
      if (scrollLeft + clientWidth + 1 > scrollWidth) {
        shiftToNextRange()
      } else if (scrollLeft <= 0) {
        shiftToPrevRange()
      }
    }
  }, [shiftToNextRange, shiftToPrevRange, roadmapRef])

  // Listen for changes to the browser window size, so we can check if it occludes the table
  const windowInnerWidth = useSyncExternalStore(subscribeToResize, () => window.innerWidth)
  // 1) If the browser width is smaller than the table width, we need to adjust the root margin to
  //    not account for the table width, because the table will be occluded by the browser window.
  //    So, the visible area will be positioned outside of the window, and the items will be seen
  //    as "not visible."
  // 2) Otherwise, if the browser can accomodate the table, then we will consider the "visible area"
  //    as the area to the right of the table.
  const rootMargin = useMemo(
    () =>
      tableWidth < windowInnerWidth
        ? `${OBSERVER_Y_MARGIN}px 0px ${OBSERVER_Y_MARGIN}px -${tableWidth}px`
        : `${OBSERVER_Y_MARGIN}px 0px ${OBSERVER_Y_MARGIN}px 0px`,
    [tableWidth, windowInnerWidth],
  )

  const tableGroups = isGrouped ? addNoGroupWhenGroupsDisabled(topLevelRows) : undefined

  const itemData = useItemData(tableInstance, {sticky: true})
  return (
    <>
      <ObserverProvider rootRef={roadmapRef} rootMargin={rootMargin} sizeEstimate={ROADMAP_ROW_HEIGHT}>
        <div className={styles.roadmapViewContainer} style={roadmapCssVariables}>
          <RoadmapControls />
          {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
          <div
            id={PROJECT_ITEMS_REGION_ID}
            ref={containerRef}
            role="grid"
            {...hpcAttrs()}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            className={styles.roadmapViewContainer}
            {...testIdProps('roadmap-view')}
          >
            <div
              ref={roadmapRef}
              onScroll={onScroll}
              className={clsx(
                {
                  'is-scrolling-locked': isScrollingLocked,
                  'is-grouped': isGrouped,
                  'is-omnibar-fixed': showFixedOmnibar,
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                  'horizontal-scrollbar-hidden': horizontalScrollbarSize === 0,
                },
                styles.roadmapScrollContainer,
              )}
              data-memex-small-viewport-a11y={memex_small_viewport_a11y}
              {...testIdProps('roadmap-scroll-container')}
            >
              <RoadmapHeader />
              <RowReorderingProvider>
                <RoadmapItemsArea isGrouped={isGrouped}>
                  <RoadmapMarkers />
                  {isGrouped && tableGroups?.length ? (
                    <GroupedRoadmapItems
                      collapsedGroupIds={collapsedGroups}
                      tableGroups={tableGroups}
                      itemData={itemData}
                    />
                  ) : (
                    <ReorderableRowsContext siblingRows={topLevelRows}>
                      <RoadmapItems rows={topLevelRows} isOmnibarFixed={showFixedOmnibar} itemData={itemData} />
                    </ReorderableRowsContext>
                  )}
                  <RowReorderSash
                    rows={isGrouped && tableGroups ? tableGroups : topLevelRows}
                    isGrouped={isGrouped}
                    scrollRef={roadmapRef}
                    headerHeight={headerHeight}
                    tableWidth={tableWidth}
                  />
                </RoadmapItemsArea>
              </RowReorderingProvider>
            </div>
            {showFixedOmnibar && hasWritePermissions && <RoadmapFixedOmnibar rows={topLevelRows} />}
          </div>
        </div>
      </ObserverProvider>
      {/* Currently only invoked via command palette */}
      {showAddFieldModal && (
        <AddColumnModal isOpen={showAddFieldModal} setOpen={setShowAddFieldModal} anchorRef={anchorRef} onSave={noop} />
      )}
    </>
  )
}

/**
 * The Roadmap area below the sticky header containing all items
 */
const RoadmapItemsArea = memo(function RoadmapItemsAreaComponent({
  isGrouped,
  children,
}: {
  isGrouped: boolean
  children: React.ReactNode
}) {
  const {totalWidth} = useRoadmapView()
  const {containerRef} = useSelectionBlur()

  return (
    <BaseStyles style={{fontSize: '14px'}}>
      <div
        className={clsx(styles.roadmapItemsArea, !isGrouped && styles.withShadow)}
        style={{width: totalWidth}}
        ref={containerRef}
        {...testIdProps('roadmap-items')}
      >
        <div className={clsx(styles.roadmapItem, !isGrouped && styles.withDelimiter)}>{children}</div>
      </div>
    </BaseStyles>
  )
})

function subscribeToResize(notify: () => void) {
  window.addEventListener('resize', notify)
  return () => window.removeEventListener('resize', notify)
}
